import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, isUndefined } from 'lodash';
import uuid from 'react-uuid';
import { COMP_GROUPS_SLUG } from 'common/constants/firms';
import { useStore } from 'common/store';
import { CustomContainer, MessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import ValuationService from 'services/valuations';
import { getInitials } from 'utilities';
import { ADD_PUBLIC_COMP_GROUP, PRIVATE_TRANSACTIONS } from '../../constants/constants';
import AccordionCompGroup from '../AccordionCompGroup';
import AddCompButton from '../AddCompButton';
import NewCompDialog from '../NewCompDialog';
import { CompGroup, CompGroupsProps, FirmCompGroupsResponse } from './CompGroups.props';

const COMP_GROUPS_CONTAINER_CLASS = `${COMP_GROUPS_SLUG}-container`;

const CompGroups = ({
  GPCCompsGroups,
  setGPCCompsGroups,
  transactionCompsGroups,
  setTransactionCompsGroups,
  firmId,
  setCompsToDelete,
  expandedState,
  setExpandedState,
  setTransactionsValidatedGrids,
  showTransactionCellsWithErrors,
}: CompGroupsProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [{ user }] = useStore();

  const saveAsGlobal = useMemo(() => user?.is_superuser && firmId === 0, [user, firmId]);

  const getFirmCompGroups = useCallback(async () => {
    if (firmId || firmId === 0) {
      const valuationSvc = new ValuationService();
      const firmCompGroups = (await valuationSvc.getFirmCompGroups(firmId)) as FirmCompGroupsResponse;

      if (firmCompGroups && !isUndefined(saveAsGlobal)) {
        // if super user we only want globals to display/update
        const filterCriteria = (group: CompGroup, type: string) =>
          group.group_type === type && (saveAsGlobal ? group.is_global : !group.is_global);

        const receivedGPC = firmCompGroups.results.filter(group => filterCriteria(group, PUBLIC_COMPANIES));
        const receivedTransactions = firmCompGroups.results.filter(group =>
          filterCriteria(group, PRIVATE_TRANSACTIONS)
        );
        setGPCCompsGroups(receivedGPC);
        setTransactionCompsGroups(receivedTransactions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmId, saveAsGlobal]);

  useEffect(() => {
    getFirmCompGroups();
  }, [getFirmCompGroups]);

  const addComp = () => {
    setOpenDialog(true);
  };

  const handleCompClose = () => {
    setOpenDialog(false);
  };

  const handleApproachTypeClick = (compGroup: string) => {
    const newCompGroup = {
      // eslint-disable-next-line radix
      id: uuid(),
      name: '',
      initials: getInitials({ name: compGroup, maxInitials: 3 }),
      is_global: saveAsGlobal,
      firm_id: saveAsGlobal ? 0 : firmId,
      version: '',
      versions: [],
    };

    if (compGroup === ADD_PUBLIC_COMP_GROUP) {
      const gpc = {
        ...newCompGroup,
        group_type: PUBLIC_COMPANIES,
        gpc_comps: [],
      };
      setGPCCompsGroups((prevState: any) => [...(prevState ?? []), gpc]);
    } else if (compGroup === 'Add Transaction Comp Group') {
      const gpt = {
        ...newCompGroup,
        group_type: PRIVATE_TRANSACTIONS,
        pt_comps: [],
      };
      if (!transactionCompsGroups) {
        setTransactionCompsGroups([]);
      }
      setTransactionCompsGroups((prevState: any) => [...(prevState ?? []), gpt]);
    }
    setOpenDialog(false);
  };

  if (isUndefined(GPCCompsGroups) && isEmpty(transactionCompsGroups)) {
    return <GridSkeleton />;
  }
  if (GPCCompsGroups?.length === 0 && isEmpty(transactionCompsGroups)) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <MessageBox
          title={`There are no ${firmId === 0 ? 'global comp groups yet' : 'comp groups associated to this firm'}.`}
          tagline="You can add one from the Add Comp Group button."
          fullWidth={false}
        />
        <NewCompDialog
          handleCompClose={handleCompClose}
          openDialog={openDialog}
          handleApproachTypeClick={handleApproachTypeClick}
        />
        <AddCompButton openDialog={openDialog} addComp={addComp} />
      </div>
    );
  }

  const accordionCompGroupsProps = {
    expandedState,
    setExpandedState,
    setCompsToDelete,
    showTransactionCellsWithErrors,
  };

  return (
    <CustomContainer className={COMP_GROUPS_CONTAINER_CLASS} rowSpacing={0}>
      {GPCCompsGroups?.map(group => (
        <AccordionCompGroup
          key={group.id}
          group={group}
          GPCCompsGroups={GPCCompsGroups}
          setGPCCompsGroups={setGPCCompsGroups}
          {...accordionCompGroupsProps}
        />
      ))}

      {transactionCompsGroups?.map(group => (
        <AccordionCompGroup
          key={group.id}
          group={group}
          transactionCompsGroups={transactionCompsGroups}
          setTransactionCompsGroups={setTransactionCompsGroups}
          setTransactionsValidatedGrids={setTransactionsValidatedGrids}
          {...accordionCompGroupsProps}
        />
      ))}

      <NewCompDialog
        handleCompClose={handleCompClose}
        openDialog={openDialog}
        handleApproachTypeClick={handleApproachTypeClick}
      />

      <AddCompButton openDialog={openDialog} addComp={addComp} />
    </CustomContainer>
  );
};

export default CompGroups;
